import type { ReactNode } from 'react';
import { cx } from './cx';
import { Text } from './Text';

/**
 * Page / section level message. Replaces the hand-rolled admin failed-encode
 * banner, form-level error notices, info strips. Tone pulls the status tokens
 * (info uses the neutral bone). A left colour bar carries the tone so the body
 * text stays readable carbon.
 */
type BannerTone = 'info' | 'success' | 'warning' | 'danger';

const SURFACE: Record<BannerTone, string> = {
  info: 'bg-bone border-border',
  success: 'bg-success-bg border-success-border',
  warning: 'bg-warning-bg border-warning-border',
  danger: 'bg-danger-bg border-danger-border',
};

const BAR: Record<BannerTone, string> = {
  info: 'border-l-text-muted',
  success: 'border-l-success',
  warning: 'border-l-warning',
  danger: 'border-l-danger',
};

export function Banner({
  tone = 'info',
  title,
  children,
  action,
  onClose,
  className,
}: {
  tone?: BannerTone;
  title?: ReactNode;
  children?: ReactNode;
  /** Optional trailing action (e.g. a <Button size="sm">). */
  action?: ReactNode;
  /** When provided, renders a close affordance. */
  onClose?: () => void;
  className?: string;
}) {
  return (
    <div
      role={tone === 'danger' || tone === 'warning' ? 'alert' : 'status'}
      className={cx(
        'flex items-start gap-3 rounded-sm border border-l-4 p-3',
        SURFACE[tone],
        BAR[tone],
        className,
      )}
    >
      <div className="flex-1">
        {title && (
          <Text role="caption" as="p" className="font-semibold text-text">
            {title}
          </Text>
        )}
        {children && (
          <Text role="small" muted as="p" className={title ? 'mt-0.5' : undefined}>
            {children}
          </Text>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          className="-m-1 shrink-0 rounded-sm p-1 text-text-muted hover:text-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <span aria-hidden="true" className="block text-[1rem] leading-none">
            ×
          </span>
        </button>
      )}
    </div>
  );
}
